import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { broadcastScheduleQueryKeys } from '@/features/broadcast-schedule/api/query-keys';
import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { BatchResponse } from '@/types/api';
import { emptyStringValues } from '@/utils/objects';

import { InfiniteSegments, TPostSegmentsBatch, TSegment } from '../types';

import { segmentsQueryKeys } from './query-keys';

export async function postSegmentsBatch(body: TPostSegmentsBatch): Promise<BatchResponse> {
  return mediaManagerClient.post('/api/v1/segments/batch', emptyStringValues(body));
}

type MutationFn = typeof postSegmentsBatch;

type TUsePostSegmentsBatchParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePostSegmentsBatch({ config }: TUsePostSegmentsBatchParams = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    ...config,
    mutationFn: postSegmentsBatch,
    onError(error, variables) {
      if (variables.action === 'update') {
        addErrorNotification(
          t('No se ha podido cambiar el estado.'),
          t('Ha habido un error en el proceso.')
        );
      } else if (variables.action === 'relaunch') {
        addErrorNotification(
          t('No se han podido relanzar los enriquecimientos.'),
          t('Ha habido un fallo en el proceso.')
        );
      }

      console.error(error);
    },
    onSuccess: async (data, variables, context) => {
      config?.onSuccess?.(data, variables, context);

      queryClient.invalidateQueries({
        queryKey: segmentsQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: broadcastScheduleQueryKeys.broadcastSchedule(),
      });

      const idToSegmentUpdate = new Map(
        variables.action === 'update' ? variables.items.map(item => [item.id, item] as const) : []
      );

      if (idToSegmentUpdate.size > 0) {
        queryClient.setQueriesData<InfiniteSegments | undefined>(
          {
            queryKey: segmentsQueryKeys.infinite(),
          },
          previousData => {
            if (!previousData) {
              return undefined;
            }

            return {
              ...previousData,
              pages: previousData.pages.map(page => ({
                ...page,
                data: page.data.map(item => ({
                  ...item,
                  review_state: idToSegmentUpdate.get(item.id)?.review_state ?? item.review_state,
                })),
              })),
            };
          }
        );

        queryClient.setQueriesData<TSegment[] | undefined>(
          {
            queryKey: [broadcastScheduleQueryKeys.broadcastSchedule()],
            predicate: query =>
              // validate the query is of type `/<program-broadcast-id>/<segment-filters>`
              typeof query.queryKey.at(-2) === 'string' &&
              typeof query.queryKey.at(-1) === 'object',
          },
          previousData => {
            if (!previousData) {
              return undefined;
            }

            return previousData.map(item => ({
              ...item,
              review_state: idToSegmentUpdate.get(item.id)?.review_state ?? item.review_state,
            }));
          }
        );
      }

      if (variables.action === 'update') {
        addSuccessNotification(
          t('Se ha cambiado el estado correctamente.'),
          t('El proceso ha sido un éxito.')
        );
      } else if (variables.action === 'relaunch') {
        addSuccessNotification(
          t('Se han relanzado los enriquecimientos.'),
          t('El proceso ha sido un éxito.')
        );
      }
    },
  });
}
